/**
 * Sales Analysis chart contract and live-response adapter.
 *
 * The backend groups paid orders and their lines into buckets and channels;
 * this module maps that response onto the chart's fixed channel shape. It
 * contains no generated data: every number comes from
 * `/butlaroo/sales-analysis`.
 */
import type {
  SalesAnalysisChannel,
  SalesAnalysisGrouping,
  SalesAnalysisPoint,
  SalesAnalysisResponse,
} from '../api/salesAnalysis'

export const SALES_CHANNELS = [
  { key: 'kiosk', label: 'Kiosk', color: '#eab308', tint: 'rgba(234, 179, 8, 0.18)' },
  { key: 'instore', label: 'In-Store', color: '#f97316', tint: 'rgba(249, 115, 22, 0.18)' },
  { key: 'uber_eats', label: 'Uber Eats', color: '#22c55e', tint: 'rgba(34, 197, 94, 0.18)' },
  { key: 'thuisbezorgd', label: 'Thuisbezorgd', color: '#f59e0b', tint: 'rgba(245, 158, 11, 0.18)' },
  { key: 'click_collect', label: 'Click & Collect', color: '#0ea5e9', tint: 'rgba(14, 165, 233, 0.18)' },
  { key: 'catering', label: 'Catering', color: '#8b5cf6', tint: 'rgba(139, 92, 246, 0.18)' },
] as const

export type SalesChannel = (typeof SALES_CHANNELS)[number]['key']

export const SALES_METRICS = [
  { key: 'sales', label: 'Sales', format: 'currency' },
  { key: 'orders', label: 'Order count', format: 'number' },
  { key: 'aov', label: 'AOV', format: 'currency' },
  { key: 'refunds', label: 'Refunds', format: 'currency' },
] as const

export type SalesMetric = (typeof SALES_METRICS)[number]['key']
export type SalesGrouping = SalesAnalysisGrouping
export type SalesScale = 'total' | 'average'

export const SALES_GROUPINGS: { key: SalesGrouping; label: string }[] = [
  { key: 'hour', label: 'Hour' },
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'weekday', label: 'Day of week' },
]

export interface SalesRaw {
  sales: number
  orders: number
  refunds: number
}

export type ChannelRaw = Record<SalesChannel, SalesRaw>

export interface SalesPoint {
  key: string
  label: string
  detail?: string
  channels: ChannelRaw
  /** Refunded amount for this bucket, if the backend supplies it. */
  refunds?: number
  /** Number of times this weekday occurs in the selected range (weekday grouping). */
  occurrences?: number
}

export interface SalesSeries {
  points: SalesPoint[]
  days: number
  /** Period totals from the backend, including any unmapped orders. */
  totals: SalesRaw
  unmappedSales: number
  unmappedOrders: number
}

export interface SalesShopRef {
  id: number
  code: string
}

const emptyRaw = (): SalesRaw => ({ sales: 0, orders: 0, refunds: 0 })

const emptyChannels = (): ChannelRaw =>
  Object.fromEntries(SALES_CHANNELS.map((channel) => [channel.key, emptyRaw()])) as ChannelRaw

const addRaw = (target: SalesRaw, source: SalesRaw): void => {
  target.sales += source.sales
  target.orders += source.orders
  target.refunds += source.refunds
}

function parseDay(iso: string): number {
  const [year, month, day] = iso.split('-').map(Number)
  return Date.UTC(year, month - 1, day)
}

export function daysInRange(from: string, to: string): number {
  const start = parseDay(from)
  const end = parseDay(to)
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return 0
  return Math.floor((end - start) / 86_400_000) + 1
}

export function availableGroupings(days: number): SalesGrouping[] {
  const available: SalesGrouping[] = ['hour', 'day']
  if (days >= 7) available.push('week')
  if (days >= 28) available.push('month')
  if (days >= 60) available.push('quarter')
  available.push('weekday')
  return available
}

export function defaultGrouping(days: number): SalesGrouping {
  if (days <= 3) return 'hour'
  if (days <= 62) return 'day'
  if (days <= 366) return 'week'
  if (days <= 1095) return 'month'
  return 'quarter'
}

export function metricValue(raw: SalesRaw, metric: SalesMetric): number {
  if (metric === 'orders') return raw.orders
  if (metric === 'aov') return raw.orders > 0 ? raw.sales / raw.orders : 0
  if (metric === 'refunds') return raw.refunds
  return raw.sales
}

export function metricDivisor(
  grouping: SalesGrouping,
  scale: SalesScale,
  days: number,
): number {
  return grouping === 'hour' && scale === 'average' ? Math.max(1, days) : 1
}

export function scaledMetric(
  raw: SalesRaw,
  metric: SalesMetric,
  divisor: number,
): number {
  if (metric === 'aov') return metricValue(raw, metric)
  return metricValue(raw, metric) / divisor
}

export function pointDivisor(
  point: SalesPoint,
  grouping: SalesGrouping,
  scale: SalesScale,
  days: number,
): number {
  if (grouping === 'weekday' && scale === 'average') {
    return Math.max(1, point.occurrences ?? 1)
  }
  return metricDivisor(grouping, scale, days)
}

/** Map the live API response onto the chart's fixed six-channel contract. */
export function toSalesSeries(response: SalesAnalysisResponse): SalesSeries {
  const points = response.points.map((point) => {
    const source = new Map(point.channels.map((channel) => [channel.key, channel]))
    const channels = emptyChannels()
    for (const channel of SALES_CHANNELS) {
      const row = source.get(channel.key)
      channels[channel.key] = {
        sales: row?.sales_eur ?? 0,
        orders: row?.orders ?? 0,
        refunds: row?.refunds_eur ?? 0,
      }
    }
    return {
      key: point.key,
      label: point.label,
      detail: point.detail ?? undefined,
      channels,
      refunds: point.refunds_eur ?? 0,
      occurrences: point.occurrences ?? undefined,
    }
  })

  return {
    points,
    days: response.days,
    totals: {
      sales: response.totals.sales_eur,
      orders: response.totals.orders,
      refunds: response.totals.refunds_eur ?? 0,
    },
    unmappedSales: response.unmapped_sales_eur,
    unmappedOrders: response.unmapped_orders,
  }
}

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]
const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function parsePointDate(key: string): { year: number; month: number; day: number; date: Date } | null {
  const [year, month, day] = key.split('-').map(Number)
  if (!year || !month || !day) return null
  const date = new Date(Date.UTC(year, month - 1, day))
  if (Number.isNaN(date.getTime())) return null
  return { year, month, day, date }
}

function emptyAnalysisChannels(): SalesAnalysisChannel[] {
  return SALES_CHANNELS.map((channel) => ({
    key: channel.key,
    label: channel.label,
    sales_eur: 0,
    orders: 0,
    refunds_eur: 0,
    aov_eur: 0,
    share_pct: 0,
  }))
}

/**
 * Re-bucket a backend **day** response entirely on the client.
 *
 * The page fetches one day series and one hour-of-day series per period/shop
 * selection. Changing Day/Week/Month/Quarter/Weekday or the metric/scale never
 * refetches: this function turns the cached day series into the requested
 * buckets and recalculates channel shares.
 */
export function aggregateDailySales(
  response: SalesAnalysisResponse,
  grouping: SalesGrouping,
): SalesAnalysisResponse {
  if (grouping === 'day' || grouping === 'hour') {
    return { ...response, grouping }
  }

  type Bucket = {
    key: string
    label: string
    detail: string
    occurrences: number | null
    sales: number
    orders: number
    refunds: number
    channels: Map<string, { sales: number; orders: number; refunds: number }>
  }
  const buckets = new Map<string, Bucket>()
  const addBucket = (bucket: Bucket): void => {
    if (!buckets.has(bucket.key)) buckets.set(bucket.key, bucket)
  }

  if (grouping === 'weekday') {
    for (const [index, label] of WEEKDAY_LABELS.entries()) {
      addBucket({
        key: `weekday-${index}`,
        label,
        detail: 'Across selected period',
        occurrences: 0,
        sales: 0,
        orders: 0,
        refunds: 0,
        channels: new Map(),
      })
    }
  }

  for (const point of response.points) {
    const parsed = parsePointDate(point.key)
    if (!parsed) continue
    let key: string
    let label: string
    let detail: string
    if (grouping === 'week') {
      const weekdayIndex = (parsed.date.getUTCDay() + 6) % 7
      const monday = new Date(parsed.date)
      monday.setUTCDate(monday.getUTCDate() - weekdayIndex)
      const year = monday.getUTCFullYear()
      const month = monday.getUTCMonth() + 1
      const day = monday.getUTCDate()
      key = `week-${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      label = `${day} ${MONTH_LABELS[month - 1]}`
      detail = 'Week starting'
    } else if (grouping === 'month') {
      key = `month-${parsed.year}-${String(parsed.month).padStart(2, '0')}`
      label = `${MONTH_LABELS[parsed.month - 1]} ${parsed.year}`
      detail = 'Calendar month'
    } else if (grouping === 'quarter') {
      const quarter = Math.floor((parsed.month - 1) / 3) + 1
      key = `quarter-${parsed.year}-${quarter}`
      label = `Q${quarter} ${parsed.year}`
      detail = 'Calendar quarter'
    } else {
      const weekdayIndex = (parsed.date.getUTCDay() + 6) % 7
      key = `weekday-${weekdayIndex}`
      label = WEEKDAY_LABELS[weekdayIndex]
      detail = 'Across selected period'
    }

    addBucket({
      key,
      label,
      detail,
      occurrences: grouping === 'weekday' ? 1 : null,
      sales: 0,
      orders: 0,
      refunds: 0,
      channels: new Map(),
    })
    const bucket = buckets.get(key)!
    bucket.sales += point.sales_eur
    bucket.orders += point.orders
    bucket.refunds += point.refunds_eur ?? 0
    if (grouping === 'weekday' && bucket.occurrences !== null) {
      bucket.occurrences += point.occurrences ?? 1
    }
    for (const channel of point.channels) {
      const current = bucket.channels.get(channel.key) ?? { sales: 0, orders: 0, refunds: 0 }
      current.sales += channel.sales_eur
      current.orders += channel.orders
      current.refunds += channel.refunds_eur ?? 0
      bucket.channels.set(channel.key, current)
    }
  }

  const points: SalesAnalysisPoint[] = Array.from(buckets.values()).map((bucket) => ({
    key: bucket.key,
    label: bucket.label,
    detail: bucket.detail,
    sales_eur: bucket.sales,
    orders: bucket.orders,
    refunds_eur: bucket.refunds,
    aov_eur: bucket.orders > 0 ? bucket.sales / bucket.orders : 0,
    occurrences: bucket.occurrences,
    channels: emptyAnalysisChannels().map((channel) => {
      const values = bucket.channels.get(channel.key)
      const sales = values?.sales ?? 0
      const orders = values?.orders ?? 0
      const refunds = values?.refunds ?? 0
      return {
        ...channel,
        sales_eur: sales,
        orders,
        refunds_eur: refunds,
        aov_eur: orders > 0 ? sales / orders : 0,
        share_pct: bucket.sales > 0 ? (sales / bucket.sales) * 100 : 0,
      }
    }),
  }))

  return { ...response, grouping, points }
}

export interface SalesSummary {
  sales: number
  orders: number
  refunds: number
  aov: number
  /** Total for the selected metric, already scaled for hourly average. */
  metricTotal: number
  divisor: number
  topChannel: { key: SalesChannel; label: string; value: number } | null
}

export function totalRaw(channels: ChannelRaw): SalesRaw {
  const total = emptyRaw()
  for (const channel of SALES_CHANNELS) addRaw(total, channels[channel.key])
  return total
}

export function pointMetricValue(
  point: SalesPoint,
  metric: SalesMetric,
  divisor: number,
): number {
  const total = totalRaw(point.channels)
  if (metric === 'refunds' && point.refunds !== undefined) total.refunds = point.refunds
  return scaledMetric(total, metric, divisor)
}

export function channelMetricValue(
  point: SalesPoint,
  channel: SalesChannel,
  metric: SalesMetric,
  divisor: number,
): number {
  return scaledMetric(point.channels[channel], metric, divisor)
}


export function summarizeSales(
  series: SalesSeries,
  metric: SalesMetric,
  grouping: SalesGrouping,
  scale: SalesScale,
): SalesSummary {
  const divisor = metricDivisor(grouping, scale, series.days)
  const byChannel = Object.fromEntries(
    SALES_CHANNELS.map((channel) => [channel.key, emptyRaw()]),
  ) as Record<SalesChannel, SalesRaw>

  for (const point of series.points) {
    for (const channel of SALES_CHANNELS) {
      addRaw(byChannel[channel.key], point.channels[channel.key])
    }
  }

  let topChannel: SalesSummary['topChannel'] = null
  for (const channel of SALES_CHANNELS) {
    const value = scaledMetric(byChannel[channel.key], metric, divisor)
    if (value > 0 && (!topChannel || value > topChannel.value)) {
      topChannel = { key: channel.key, label: channel.label, value }
    }
  }

  const weekdayAverage =
    grouping === 'weekday' && scale === 'average'
      ? (() => {
          const active = series.points.filter((point) => (point.occurrences ?? 0) > 0)
          if (active.length === 0) return 0
          return (
            active.reduce(
              (sum, point) =>
                sum + pointMetricValue(point, metric, Math.max(1, point.occurrences ?? 1)),
              0,
            ) / active.length
          )
        })()
      : null

  return {
    sales: series.totals.sales,
    orders: series.totals.orders,
    refunds: series.totals.refunds,
    aov: series.totals.orders > 0 ? series.totals.sales / series.totals.orders : 0,
    metricTotal: weekdayAverage ?? scaledMetric(series.totals, metric, divisor),
    divisor,
    topChannel,
  }
}

